import { useForm } from "react-hook-form";
import moment, { Moment } from "moment";
import { makeStyles } from "tss-react/mui";
import z from "zod";
import {
  MiAbbreviationBadge,
  MiCard,
  MiControlledTextField,
  MiDatePicker,
  MiTimePicker,
} from "@miplatform/components";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiCalendarBlankOutline, mdiFileDocumentOutline } from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { Box, Typography } from "@mui/material";
import {
  CreateWorkOrderForm,
  CustomWorkTemplateBase,
} from "./CreateWorkOrderDrawer.constants";

interface WorkOrderCreationProps {
  template: CustomWorkTemplateBase;
  isLoading?: boolean;
  onSubmit: (data: CreateWorkOrderForm) => void;
  onClose: () => void;
}

export const WorkOrderCreation: React.FC<WorkOrderCreationProps> = ({
  template,
  isLoading = false,
  onSubmit,
  onClose,
}) => {
  const { theme, classes } = useStyles();
  const {
    watch,
    setValue,
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<CreateWorkOrderForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      dueDate: null,
      dueDateTime: null,
      notes: "",
    },
  });
  const dueDate = watch("dueDate");
  const dueDateTime = watch("dueDateTime");

  const handleDueDateChange = (date: Moment | null) =>
    setValue("dueDate", date, { shouldValidate: true, shouldDirty: true });

  const handleDueDateTimeChange = (date: Moment | null) =>
    setValue("dueDateTime", date, { shouldValidate: true, shouldDirty: true });

  return (
    <Box className={classes.root}>
      <Box flex={1}>
        <MiCard
          containerStyles={{
            display: "flex",
            flexDirection: "column",
            padding: theme.spacing(0, 4),
            margin: theme.spacing(0, 3),
            gap: theme.spacing(3),
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <MiAbbreviationBadge
              abbreviation={template.abbreviation as string}
              backgroundColor={template.color as string}
            />
            <Typography fontWeight={theme.fontWeight.semiBold}>
              {template.name}
            </Typography>
          </Box>
          <MiCard
            leftIcon={mdiCalendarBlankOutline}
            title="Date Information"
            containerStyles={{}}
          >
            <Box display="flex" gap={2}>
              <Box width="100%">
                <MiDatePicker
                  required
                  whiteBackground
                  label="Date"
                  value={dueDate}
                  format={DATE_TIME_FORMATS.FORMAT_MMMM_D_YYYY}
                  error={errors.dueDate?.message}
                  onChange={handleDueDateChange}
                />
              </Box>
              <Box width="100%">
                <MiTimePicker
                  required
                  label="Due Time"
                  value={dueDateTime}
                  format={DATE_TIME_FORMATS.HOURS_MINUTES}
                  error={errors.dueDateTime as { message: string } | undefined}
                  sx={{ backgroundColor: theme.palette.background.default }}
                  onChange={handleDueDateTimeChange}
                />
              </Box>
            </Box>
          </MiCard>
          <MiCard
            leftIcon={mdiFileDocumentOutline}
            title="Notes"
            containerStyles={{
              paddingBottom: theme.spacing(5),
            }}
          >
            <MiControlledTextField
              name="notes"
              label="Notes"
              formControl={control}
              sx={{ backgroundColor: theme.palette.background.default }}
            />
          </MiCard>
        </MiCard>
      </Box>
      <Footer
        show
        saveTitle="Save and Create Work Order"
        handleCancelClick={onClose}
        disableSave={isLoading || !isValid}
        handleSaveClick={handleSubmit(onSubmit)}
      />
    </Box>
  );
};

const momentSchema = z.custom<Moment | null>(
  (val) => val === null || moment.isMoment(val),
  { message: "Invalid date format" }
);

const schema = z
  .object({
    dueDate: momentSchema.refine((val) => val !== null, {
      message: "Due date is required",
    }),

    dueDateTime: momentSchema,

    notes: z
      .string()
      .max(500, "Notes cannot exceed 500 characters")
      .optional()
      .default(""),
  })
  .refine(
    (data) => {
      return data.dueDateTime?.isValid();
    },
    {
      message: "Due time is required",
      path: ["dueDateTime"],
    }
  );

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    flex: 1,
    height: "100%",
    paddingTop: theme.spacing(2),
  },
}));
